import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '../../../shared/ui/button';
import { Input } from '../../../shared/ui/input';
import { Note } from '../../../shared/ui/note';
import { copyRequest, copyRequestRaw } from '../../../entities/run';
import type { ISampleDraft } from '../model';

export const TakeFromStream = ({ onTake }: { onTake: (next: ISampleDraft) => void }) => {
  const [id, setId] = useState('');
  const [asked, setAsked] = useState('');

  const raw = useQuery({
    queryKey: ['copy-request-raw', asked],
    // Two reads: the request's line and headers, and its body bytes.
    queryFn: async () => {
      const [request, body] = await Promise.all([copyRequest(asked), copyRequestRaw(asked)]);
      return {
        ...request,
        httpMethod: request.method,
        query: request.query ?? '',
        text: body.text,
      };
    },
    enabled: asked.length > 0,
  });

  useEffect(() => {
    const data = raw.data;
    if (!data || !asked) return;

    onTake({
      headersText: data.headers.map((header) => `${header.name}: ${header.value}`).join('\n'),
      body: data.text ?? '',
      httpMethod: data.httpMethod,
      path: data.path,
      query: data.query,
    });
    // Cleared so a second press cannot silently overwrite an edited sample.
    setAsked('');
    setId('');
  }, [raw.data, asked, onTake]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-end gap-2">
        <Input
          size="row"
          mono
          value={id}
          placeholder="evt_…"
          aria-label="Event id to take the request from"
          onChange={(event) => setId(event.target.value)}
          className="flex-1"
        />
        <Button
          variant="ghost"
          size="sm"
          disabled={!id || raw.isFetching}
          onClick={() => setAsked(id.trim())}
        >
          {raw.isFetching ? 'Taking' : 'Take one from the stream'}
        </Button>
      </div>
      {raw.isError && (
        <Note tone="danger">
          No stored request for that event. It may have been removed by retention.
        </Note>
      )}
    </div>
  );
};
